import Moment from "moment";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import DatePicker from "react-datepicker";
import { put } from "aws-amplify/api";
import { Card, Col, Form, Row, Table } from "react-bootstrap";
import "./SaleSheetFollowUpCard.css";
import "react-datepicker/dist/react-datepicker.css";

function formatTableDate(value) {
  if (!value) {
    return "";
  }

  const parsed = Moment(value);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : "";
}

function formatAddress(property) {
  if (!property) {
    return "";
  }

  const knownAs = (property.known_as || "").trim();
  if (knownAs.length > 0) {
    return knownAs;
  }

  const low = `${property.street_num_low || ""}${property.street_num_low_suffix || ""}`.trim();
  const high = `${property.street_num_high || ""}${property.street_num_high_suffix || ""}`.trim();
  const range = low && high && low !== high ? `${low} - ${high}` : low || high;
  return [range, property.street_name || ""].filter(Boolean).join(" ").trim();
}

function ContactBlock({ title, name, phone, email }) {
  return (
    <div className="salesheet-followup-contact-block">
      <div className="salesheet-followup-contact-title">{title}</div>
      <div>{name || ""}</div>
      <div><strong>t:</strong> {phone || ""}</div>
      <div><strong>e:</strong> {email || ""}</div>
    </div>
  );
}

function formatOwners(property) {
  const owners = property?.property_owners;
  if (!Array.isArray(owners) || owners.length === 0) {
    return [];
  }

  return owners.filter((owner) => owner?.owner_name && owner?.owner_id);
}

function formatSalesHistory(property) {
  const salesText = property?.sales_text;
  if (!salesText) {
    return "";
  }

  return salesText.length > 200 ? `...${salesText.slice(-200)}` : salesText;
}

function formatLegacyPropertyHeader(property) {
  const propertyKey = property?.property_key ?? "";
  const suburb = property?.suburb ?? "";
  const buildingName = property?.building_name ?? "";

  const address = (() => {
    const knownAs = (property?.known_as ?? "").trim();
    if (knownAs.length > 0) {
      return knownAs;
    }

    const low = `${property?.street_num_low ?? ""}${property?.street_num_low_suffix ?? ""}`.trim();
    const high = `${property?.street_num_high ?? ""}${property?.street_num_high_suffix ?? ""}`.trim();
    const range = low && high && low !== high ? `${low} - ${high}` : low || high;
    const streetName = property?.street_name ?? "";
    return [range, streetName].filter(Boolean).join(" ").trim();
  })();

  return [propertyKey, address, suburb, buildingName].filter(Boolean).join(" | ");
}

function PropertyDetailBlock({ property }) {
  const owners = formatOwners(property);

  return (
    <div className="salesheet-followup-property-block">
      <div className="salesheet-followup-detail-line">
        <strong>Cityscope Owner(s):</strong>{" "}
        <div>
        {owners.map((owner) => (
          <React.Fragment key={owner.owner_id}>
            <a target="_blank" rel="noopener noreferrer" href={`../../owners/${owner.owner_id}`}>
              {owner.owner_name}
            </a>{" | "}
          </React.Fragment>
        ))}
        </div>
      </div>
      <Table hover size="sm" responsive className="salesheet-followup-property-table">
        <thead>
          <tr>
            <th>Cityscope Reference</th>
            <th>Building</th>
            <th>Street Address</th>
            <th>Suburb</th>
            <th>Classification</th>
            <th>Sale Date</th>
            <th>Sale Price</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              {property?.property_key ? (
                <a target="_blank" rel="noopener noreferrer" href={`../../properties/${property.property_key}`}>
                  {property.property_key}
                </a>
              ) : ""}
            </td>
            <td>{property?.building_name || ""}</td>
            <td>{formatAddress(property)}</td>
            <td>{property?.suburb || ""}</td>
            <td>{property?.classification || ""}</td>
            <td>
              {property?.sale_id && property?.sale_date ? (
                <a target="_blank" rel="noopener noreferrer" href={`../../sales/${property.sale_id}`}>
                  {formatTableDate(property.sale_date)}
                </a>
              ) : formatTableDate(property?.sale_date)}
            </td>
            <td>{property?.sale_price || ""}</td>
          </tr>
        </tbody>
      </Table>
      <div className="salesheet-followup-detail-line">
        <strong>Description:</strong> {property?.description || ""}
      </div>
      <div className="salesheet-followup-detail-line">
        <strong>Title Details:</strong> {property?.title_details || ""}
      </div>
      <div className="salesheet-followup-detail-line">
        <strong>Sales History:</strong> {formatSalesHistory(property)}
      </div>
    </div>
  );
}

export default function SaleSheetFollowUpCard({ salesheet, saleStatusOptions: providedSaleStatusOptions = [] }) {
  const primaryProperty = useMemo(() => salesheet?.properties?.[0] || {}, [salesheet]);
  const properties = useMemo(() => salesheet?.properties || [], [salesheet]);

  const hasPropertyData = useMemo(() => {
    return properties.some((property) => {
      const hasOwners = Array.isArray(property?.property_owners) && property.property_owners.length > 0;
      const valuesToCheck = [
        property?.property_key,
        property?.known_as,
        property?.street_num_low,
        property?.street_num_high,
        property?.street_name,
        property?.suburb,
        property?.building_name,
        property?.classification,
        property?.sale_date,
        property?.sale_price,
        property?.description,
        property?.title_details,
        property?.sales_text,
      ];

      return hasOwners || valuesToCheck.some((value) => {
        if (value === null || value === undefined) {
          return false;
        }

        return String(value).trim() !== "";
      });
    });
  }, [properties]);

  const [quick_ref, setQuickRef] = useState("");
  const [sale_status_code, setSaleStatusCode] = useState("");
  const [sale_status_date_checked, setSaleStatusDateChecked] = useState(null);
  const [sale_follow_up_date, setSaleFollowUpDate] = useState(null);
  const [current, setCurrent] = useState(false);
  const [current_questions, setCurrentQuestions] = useState("");
  const [sales_notes, setSalesNotes] = useState("");

  const saleStatusOptions = useMemo(
    () => (Array.isArray(providedSaleStatusOptions) ? providedSaleStatusOptions : []),
    [providedSaleStatusOptions]
  );

  const selectedSaleStatusLabel = useMemo(() => {
    const selected = saleStatusOptions.find((option) => option.value === sale_status_code);
    return selected?.label || "";
  }, [saleStatusOptions, sale_status_code]);

  useEffect(() => {
    if (salesheet) {
      setQuickRef(salesheet.quick_ref || "");
      setSaleStatusCode(
        salesheet.sale_status_code === null || salesheet.sale_status_code === undefined
          ? ""
          : String(salesheet.sale_status_code)
      );
      setSaleStatusDateChecked(
        salesheet.sale_status_date_checked ? Moment(salesheet.sale_status_date_checked).toDate() : null
      );
      setSaleFollowUpDate(
        salesheet.sale_follow_up_date ? Moment(salesheet.sale_follow_up_date).toDate() : null
      );
      setCurrent(Boolean(salesheet.current));
      setCurrentQuestions(salesheet.current_questions || "");
      setSalesNotes(salesheet.sales_notes || "");
    }
  }, [salesheet]);

  useEffect(() => {
    if (!sale_status_code && salesheet?.sale_status && saleStatusOptions.length > 0) {
      const match = saleStatusOptions.find((option) => option.label === salesheet.sale_status);
      if (match) {
        setSaleStatusCode(match.value);
      }
    }
  }, [sale_status_code, salesheet?.sale_status, saleStatusOptions]);

  const handleSave = useCallback(async () => {
    if (!salesheet?.sale_sheet_id) {
      return;
    }

    try {
      await put({
        apiName: "colin",
        path: `/salesheets/${salesheet.sale_sheet_id}`,
        options: {
          body: {
            sale_sheet_id: salesheet.sale_sheet_id,
            quick_ref,
            sale_status_code: sale_status_code === "" ? null : sale_status_code,
            sale_status: selectedSaleStatusLabel,
            sale_status_date_checked: sale_status_date_checked ? Moment(sale_status_date_checked).format() : null,
            sale_follow_up_date: sale_follow_up_date ? Moment(sale_follow_up_date).format() : null,
            current,
            current_questions,
            sales_notes,
          },
        },
      }).response;
    } catch (error) {
      console.error("Error saving salesheet:", error);
    }
  }, [salesheet?.sale_sheet_id, quick_ref, sale_status_code, selectedSaleStatusLabel, sale_status_date_checked, sale_follow_up_date, current, current_questions, sales_notes]);

  const cardHeading = useMemo(() => {
    return formatLegacyPropertyHeader(primaryProperty);
  }, [primaryProperty]);

  const hasTopLevelData = useMemo(() => {
    const valuesToCheck = [
      salesheet?.sale_agent_sort,
      salesheet?.sale_agent_1,
      salesheet?.sale_agent_2,
      quick_ref,
      sale_status_code,
      current_questions,
      sales_notes,
      sale_status_date_checked,
      sale_follow_up_date,
      current,
    ];

    return valuesToCheck.some((value) => {
      if (typeof value === "boolean") {
        return value;
      }

      if (value === null || value === undefined) {
        return false;
      }

      return String(value).trim() !== "";
    });
  }, [salesheet?.sale_agent_sort, salesheet?.sale_agent_1, salesheet?.sale_agent_2, quick_ref, sale_status_code, current_questions, sales_notes, sale_status_date_checked, sale_follow_up_date, current]);

  if (!hasPropertyData && !hasTopLevelData) {
    return (
      <Card className="mb-3 salesheet-followup-card">
        <Card.Body>
            <span responsive className="salesheet-followup-empty-cell">No Data to display</span>
          {/* <Table hover size="sm" responsive className="salesheet-followup-property-table">
            <tbody>
              <tr>
                <td className="salesheet-followup-empty-cell">No Data to display</td>
              </tr>
            </tbody>
          </Table> */}
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-3 salesheet-followup-card">
      <Card.Header className="salesheet-followup-card-header">
        <Card.Title className="salesheet-followup-card-title">
          <Row className="align-items-center g-0">
            <Col>
              <small className="salesheet-followup-card-title-heading">{cardHeading || ""}</small>
            </Col>
            <Col className="text-end salesheet-followup-card-agent-group">
              <small className="salesheet-followup-card-title-agent"><strong>{salesheet?.sale_agent_sort || ""}</strong></small>
            </Col>
          </Row>
        </Card.Title>
      </Card.Header>

      <Card.Body>
        <Row className="g-3">
          <Col md={4} lg={3}>
            <Form.Group as={Row} className="mb-2 align-items-center">
              <Form.Label column sm={5} className="salesheet-followup-label">Quick Reference</Form.Label>
              <Col>
                <Form.Control
                  value={quick_ref}
                  onChange={(e) => setQuickRef(e.target.value)}
                  onBlur={handleSave}
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-2 align-items-center">
              <Form.Label column sm={5} className="salesheet-followup-label">Sales Status</Form.Label>
              <Col>
                <Form.Select
                  value={sale_status_code}
                  onChange={(e) => setSaleStatusCode(e.target.value)}
                  onBlur={handleSave}
                >
                  {saleStatusOptions.map((option) => (
                    <option key={option.value || option.label} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-2 align-items-center">
              <Form.Label column sm={5} className="salesheet-followup-label">Date Last Followed Up</Form.Label>
              <Col>
                <DatePicker
                  selected={sale_status_date_checked}
                  onChange={(date) => setSaleStatusDateChecked(date)}
                  onBlur={handleSave}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  isClearable
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-2 align-items-center">
              <Form.Label column sm={5} className="salesheet-followup-label">Date Next Follow Up</Form.Label>
              <Col>
                <DatePicker
                  selected={sale_follow_up_date}
                  onChange={(date) => setSaleFollowUpDate(date)}
                  onBlur={handleSave}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  isClearable
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-2 align-items-center salesheet-followup-current-row">
              <Col sm={5}>
              </Col>
              <Col>
                <Form.Check
                  className="salesheet-followup-current-check"
                  type="checkbox"
                  label="Current"
                  checked={current}
                  onChange={(e) => {
                    setCurrent(e.target.checked);
                  }}
                  onBlur={handleSave}
                />
              </Col>
            </Form.Group>
          </Col>

          <Col md={4} lg={3}>
            <div className="salesheet-followup-contact-block">
              <div className="salesheet-followup-contact-title">Agent 1</div>
              <div>{salesheet?.sale_agent_1 || ""}</div>
              <div><strong>t:</strong> {salesheet?.sale_agent_1_phone || ""}</div>
            </div>
            <ContactBlock
              title="Contact 1"
              name={salesheet?.contact_1_sale_agent_1}
              phone={salesheet?.contact_1_sale_agent_1_phone}
              email={salesheet?.contact_1_sale_agent_1_email}
            />
            <ContactBlock
              title="Contact 2"
              name={salesheet?.contact_2_sale_agent_1}
              phone={salesheet?.contact_2_sale_agent_1_phone}
              email={salesheet?.contact_2_sale_agent_1_email}
            />
          </Col>

          <Col md={4} lg={3}>
            <div className="salesheet-followup-contact-block">
              <div className="salesheet-followup-contact-title">Agent 2</div>
              <div>{salesheet?.sale_agent_2 || ""}</div>
              <div><strong>t:</strong> {salesheet?.sale_agent_2_phone || ""}</div>
            </div>
            <ContactBlock
              title="Contact 1"
              name={salesheet?.contact_1_sale_agent_2}
              phone={salesheet?.contact_1_sale_agent_2_phone}
              email={salesheet?.contact_1_sale_agent_2_email}
            />
            <ContactBlock
              title="Contact 2"
              name={salesheet?.contact_2_sale_agent_2}
              phone={salesheet?.contact_2_sale_agent_2_phone}
              email={salesheet?.contact_2_sale_agent_2_email}
            />
          </Col>
        </Row>

        <Row className="mt-2">
          <Col>
            <Form.Group>
              <Form.Label className="salesheet-followup-label"><strong>Questions to ask Agent</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={current_questions}
                onChange={(e) => setCurrentQuestions(e.target.value)}
                onBlur={handleSave}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col>
            <Form.Group>
              <Form.Label className="salesheet-followup-label"><strong>Sales Sheet Notes</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={sales_notes}
                onChange={(e) => setSalesNotes(e.target.value)}
                onBlur={handleSave}
              />
            </Form.Group>
          </Col>
        </Row>

        {properties.length > 0 ? (
          properties.map((property, index) => (
            <Row className={index === 0 ? "mt-3" : "mt-2"} key={property?.property_id || `${property?.property_key || "property"}-${index}`}>
              <Col>
                <PropertyDetailBlock property={property} />
              </Col>
            </Row>
          ))
        ) : (
          <Row className="mt-3">
            <Col>
              <Table bordered hover size="sm" responsive className="salesheet-followup-property-table">
                <thead>
                  <tr>
                    <th>Cityscope Reference</th>
                    <th>Building</th>
                    <th>Street Address</th>
                    <th>Suburb</th>
                    <th>Classification</th>
                    <th>Sale Date</th>
                    <th>Sale Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={7}>No Data to display</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
}
